import { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Button } from './ui';
import { api } from '../lib/api';

/**
 * Distress / loan panel for a property (from Capitalize data). Renders nothing when
 * the property has no loans. Shows loan + owner contact and a Push-to-GHL action.
 */

interface Props {
  propertyId: string;
}

const money = (n: number | null | undefined) =>
  n == null ? '—' : '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
const date = (d: string | null | undefined) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US') : '—');

function maturityBadge(m: string | null): { label: string; cls: string } {
  if (!m) return { label: 'Maturity unknown', cls: 'bg-gray-100 text-gray-600' };
  const past = new Date(m + 'T00:00:00') < new Date();
  return past
    ? { label: 'Past due', cls: 'bg-primary-100 text-primary-500' }
    : { label: 'Maturing', cls: 'bg-warning-wash text-warning' };
}

export default function PropertyLoanPanel({ propertyId }: Props) {
  const [loans, setLoans] = useState<any[] | null>(null);
  const [pushing, setPushing] = useState(false);
  const [pushed, setPushed] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api.getPropertyLoans(propertyId).then((r) => { if (alive) setLoans(r.loans || []); }).catch(() => { if (alive) setLoans([]); });
    return () => { alive = false; };
  }, [propertyId]);

  const push = useCallback(async () => {
    setPushing(true); setPushed(null);
    try {
      const res = await api.pushLoansToGhl([propertyId]);
      setPushed(res.pushed > 0 ? 'Owner added to GoHighLevel for outreach.' : (res.note || 'No reachable contact (all Do-Not-Call).'));
    } catch (e: any) {
      setPushed('Push failed: ' + (e?.message || 'error'));
    } finally { setPushing(false); }
  }, [propertyId]);

  if (!loans || loans.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle>🏦 Loan / Distress <span className="text-xs font-normal text-gray-400">· Capitalize</span></CardTitle>
          <Button variant="outline" className="text-sm py-1" onClick={push} disabled={pushing}>
            {pushing ? 'Pushing…' : '→ Push owner to GHL'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {pushed && <p className="mb-3 text-xs rounded-lg bg-info-wash text-info px-3 py-2">{pushed}</p>}
        <div className="space-y-4">
          {loans.map((l) => {
            const b = maturityBadge(l.maturity_date);
            return (
              <div key={l.id} className="rounded-xl border border-primary-200 bg-white p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-gray-900">{money(l.mortgage_amount) !== '—' ? money(l.mortgage_amount) : l.mortgage_amount_raw || '—'} loan</span>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${b.cls}`}>{b.label} · {date(l.maturity_date)}</span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                  <div><span className="text-gray-400">Lender</span><br /><span className="font-medium text-gray-900">{l.lender_name || '—'}</span></div>
                  <div><span className="text-gray-400">Rate / LTV</span><br /><span className="font-medium text-gray-900">{l.interest_rate != null ? l.interest_rate + '%' : '—'} / {l.ltv != null ? l.ltv + '%' : '—'}</span></div>
                  <div><span className="text-gray-400">Originated</span><br /><span className="font-medium text-gray-900">{date(l.origination_date)}</span></div>
                  <div><span className="text-gray-400">Type</span><br /><span className="font-medium text-gray-900">{l.property_type || '—'}{l.property_subtype ? ` · ${l.property_subtype}` : ''}</span></div>
                </div>
                {/* Owner contact */}
                <div className="mt-3 border-t border-gray-100 pt-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Owner</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                    <div><span className="text-gray-400">Name</span><br /><span className="font-medium text-gray-900">{l.owner_name || l.owner_llc || '—'}</span></div>
                    <div><span className="text-gray-400">Email</span><br /><span className="font-medium text-gray-900 break-all">{l.owner_email || '—'}</span></div>
                    <div>
                      <span className="text-gray-400">Mobile</span><br />
                      <span className="font-medium text-gray-900">{l.owner_phone_mobile || '—'}{l.owner_phone_mobile_dnc ? <span className="ml-1 text-[10px] text-primary-500">(DNC)</span> : null}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Direct</span><br />
                      <span className="font-medium text-gray-900">{l.owner_phone_direct || '—'}{l.owner_phone_direct_dnc ? <span className="ml-1 text-[10px] text-primary-500">(DNC)</span> : null}</span>
                    </div>
                  </div>
                  {l.capitalize_link && (
                    <a href={l.capitalize_link} target="_blank" rel="noreferrer" className="mt-2 inline-block text-[11px] text-info hover:underline">View on Capitalize ↗</a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-[11px] text-gray-400">Source: Capitalize borrower data. Do-Not-Call numbers are excluded from outreach.</p>
      </CardContent>
    </Card>
  );
}
